import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import axios from "../../config/axios";
import { toast } from "react-toastify";

const AdminProfile = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwForm, setPwForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get("/auth/me");
        setAdmin(res.data);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to fetch profile");
        setError(err.response?.data?.message || "Failed to fetch profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    if (pwError || error) {
      const timer = setTimeout(() => {
        setError("");
        setPwError("");
        setPwSuccess("");
        setSuccess("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [pwError, error, pwSuccess, success]);

  const handlePwChange = (e) => {
    setPwForm({ ...pwForm, [e.target.name]: e.target.value });
    setPwError("");
    setPwSuccess("");
  };

  const validateForm = () => {
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/;
    if (!pwForm.newPassword || !passwordRegex.test(pwForm.newPassword)) {
      setPwError(
        "Password must be minimum 8 characters long and should contain atleast one uppercase, one lowercase and one special character."
      );
      return false;
    }
    if (!pwForm.confirmNewPassword) {
      setPwError("Confirm new password is required.");
      return false;
    }
    if (pwForm.confirmNewPassword !== pwForm.newPassword) {
      setPwError("Confirm password must match the entered new password.");
      return false;
    }
    return true;
  };

  const handlePwSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setPwError("");
    setPwSuccess("");
    if (!pwForm.oldPassword || !pwForm.newPassword) {
      setPwError("All fields are required.");
      return;
    }
    try {
      setPwLoading(true);
      const response = await axios.post("/admin/change-password", pwForm);
      setPwSuccess("Password changed successfully.");
      setPwForm({ oldPassword: "", newPassword: "", confirmNewPassword: "" });
      toast.success(response.data.message);
    } catch (err) {
      toast.error(error.response?.data?.message || "Failed to change password");
      setPwError(err.response?.data?.message || "Failed to change password");
    } finally {
      setPwLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: "#26A69A" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: "100vh", background: "#f7fafc" }}>
      <Paper
        elevation={3}
        sx={{ p: 4, maxWidth: 500, mx: "auto", borderRadius: 4 }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", color: "#26A69A", mb: 2 }}
        >
          Admin Profile
        </Typography>
        {/* {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )} */}
        {admin && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
            >
              Email:{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 500, color: "text.primary" }}
              >
                {admin.email}
              </Typography>
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
            >
              Role:{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 500, color: "text.primary" }}
              >
                {admin.role.charAt(0).toUpperCase() + admin.role.slice(1)}
              </Typography>
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
            >
              Status:{" "}
              <Typography
                component="span"
                sx={{
                  fontWeight: 500,
                  color: admin.status === "active" ? "green" : "red",
                }}
              >
                {admin.status.charAt(0).toUpperCase() + admin.status.slice(1)}
              </Typography>
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
            >
              Joined:{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 500, color: "text.primary" }}
              >
                {new Date(admin.created_at).toLocaleString()}
              </Typography>
            </Typography>
          </Box>
        )}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ color: "#26A69A", mb: 2 }}>
          Change Password
        </Typography>
        {pwError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {pwError}
          </Alert>
        )}
        {pwSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {pwSuccess}
          </Alert>
        )}
        <form onSubmit={handlePwSubmit} autoComplete="off">
          <TextField
            label="Old Password"
            name="oldPassword"
            type="password"
            value={pwForm.oldPassword}
            onChange={handlePwChange}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                background: "#F5F5F5",
                transition: "all 0.3s",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#26A69A",
                boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
              },
              "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
              "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
              mb: 2,
            }}
          />
          <TextField
            label="New Password"
            name="newPassword"
            type="password"
            value={pwForm.newPassword}
            onChange={handlePwChange}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                background: "#F5F5F5",
                transition: "all 0.3s",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#26A69A",
                boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
              },
              "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
              "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
              mb: 2,
            }}
          />
          <TextField
            label="Confirm New Password"
            name="confirmNewPassword"
            type="password"
            value={pwForm.confirmNewPassword}
            onChange={handlePwChange}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                background: "#F5F5F5",
                transition: "all 0.3s",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#26A69A",
                boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
              },
              "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
              "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
              mb: 2,
            }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={pwLoading}
            sx={{ background: "#26A69A", mb: 2, fontWeight: 600 }}
          >
            {pwLoading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              "Change Password"
            )}
          </Button>
        </form>
        <Divider sx={{ my: 3 }} />
      </Paper>
    </Box>
  );
};

export default AdminProfile;
